import React, { useState, useEffect, useRef } from "react";
import { useAxiosRQ, apiPost } from "../../api/apiREST";
import { Notification } from "../../types/notification";

interface NotificationIconProps {
  className?: string;
}

const NotificationIcon: React.FC<NotificationIconProps> = ({
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch notifications
  const { data: notificationData, refetch: refetchNotifications } = useAxiosRQ(
    "/notifications/history/",
    "notifications"
  );

  // Update notifications and unread count when data changes
  useEffect(() => {
    if (notificationData) {
      setNotifications(notificationData);
      const unread = notificationData.filter(
        (notif: Notification) => !notif.is_read
      );
      setUnreadCount(unread.length);
    }
  }, [notificationData]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleIconClick = () => {
    setIsOpen(!isOpen);
  };

  const markAllAsRead = async () => {
    try {
      const response = await apiPost("/notifications/mark-all-read/", {});
      if (response.data) {
        // Refetch notifications to update the state
        refetchNotifications();
      }
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor(now.getTime() - date.getTime()) / 1000;

    if (diffInSeconds < 60) {
      return "همین الان";
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} دقیقه پیش`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ساعت پیش`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} روز پیش`;
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Notification Icon */}
      <button
        onClick={handleIconClick}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
        aria-label="اعلان‌ها"
      >
        <svg
          className="w-5 h-5 max-sm:w-4 max-sm:h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-5-5V7a6 6 0 10-12 0v5l-5 5h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* Unread Count Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute left-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">اعلان‌ها</h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-HelpText/11/Light text-primary-600 hover:text-primary-800 hover:animate-pulse hover:scale-125 transition-all duration-100"
                >
                  خوانده شد
                </button>
              )}
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.filter((notif) => !notif.is_read).length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                اعلان خوانده نشده‌ای وجود ندارد
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications
                  .filter((notif) => !notif.is_read)
                  .map((notification) => (
                    <div
                      key={notification.id}
                      className={`px-4 py-2 hover:bg-gray-50 transition-colors duration-150 ${
                        !notification.is_read ? "bg-blue-50" : ""
                      }`}
                    >
                      <div className="flex items-start space-x-2 space-x-reverse">
                        {/* Status Indicator */}
                        <div className="flex-shrink-0 mt-1">
                          {!notification.is_read ? (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          ) : (
                            <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0 pr-1">
                          <div className="flex items-center justify-between">
                            <p className="text-BodyText/14/Regular font-medium text-gray-900">
                              {notification.title}
                            </p>
                            <p className="text-HelpText/11/Light text-gray-500 flex-shrink-0 mr-2">
                              {formatDate(notification.sent_at)}
                            </p>
                          </div>
                          <p className="text-BodyText/12/Regular text-gray-600 mt-1 leading-relaxed">
                            {notification.body}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.filter((notif) => !notif.is_read).length > 0 && (
            <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
              <p className="text-xs text-gray-500 text-center">
                {notifications.filter((notif) => !notif.is_read).length} اعلان
                خوانده نشده
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationIcon;
